import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Stack,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HtmlEditor, { Toolbar, Item, Mention } from 'devextreme-react/html-editor';
import { ValueChangedEvent } from 'devextreme/ui/html_editor';
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { MentionableUser, MentionedUser } from "../../api-client-nswag/create-sites-client";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import "./MessagesDialog.scss";

export interface Message {
  id: string;
  content: string;
  authorName: string;
  authorId: string;
  timestamp: Date;
  isCurrentUser: boolean;
  created?: Date;
}

interface MessagesDialogProps {
  open: boolean;
  onClose: () => void;
  projectName: string;
  entity: string;
  projectIdMdm: number;
  siteId: number;
  currentUserId: string;
  currentUserEmail: string;
  siteUsers: MentionableUser[];
  canSendMessage: boolean;
}

const MessagesDialog: React.FC<MessagesDialogProps> = ({
  open,
  onClose,
  projectName,
  entity,
  projectIdMdm,
  siteId,
  currentUserId,
  currentUserEmail,
  siteUsers,
  canSendMessage,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState("");
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const { showSnackbar } = useSnackbar();

  const mentionDataSource = useMemo(() => siteUsers ?? [], [siteUsers]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadMessages = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response:any = await createSitesClientApi.tim_Message(siteId, projectIdMdm);

      const messagesData = response?.data?.data?.messages || [];
      const mappedMessages: Message[] = messagesData.map((msg: any, index: number) => ({
        id: msg.created ? `${new Date(msg.created).toISOString()}-${index}` : `msg-${index}`,
        content: msg.message || "",
        authorName: msg.user || "Unknown",
        authorId: msg.userType === "Client" ? "client" : "user",
        timestamp: msg.created ? new Date(msg.created) : new Date(),
        isCurrentUser: msg.userEmail === currentUserEmail, // Compare based on userEmail
        created: msg.created,
      }));

      setMessages(mappedMessages);
    } catch (error) {
      console.error("Failed to load messages:", error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Extract mentioned users (name + email) from message HTML
  const extractMentionedUsers = (html: string): MentionedUser[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionSpans = doc.querySelectorAll('.dx-mention');
    const mentionedMap = new Map<string, MentionedUser>();
    mentionSpans.forEach(span => {
      const email = span.getAttribute('data-id');
      const name = span.getAttribute('data-mention-value') || span.textContent?.replace('@', '').trim() || email || '';
      if (email && !mentionedMap.has(email)) {
        mentionedMap.set(email, { email, name });
      }
    });
    return Array.from(mentionedMap.values());
  };

  // Load messages when dialog opens
  useEffect(() => {
    if (open) {
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectIdMdm, siteId]);

  const handleSendMessage = async () => {
    if (!canSendMessage) return;
    if (!messageText.trim() || sending) return;

    try {
      setSending(true);
      const mentionedUsers = extractMentionedUsers(messageText);

      if (editingMessageId) {
        // Update existing message - optimistic update
        const messageToUpdate = messages.find((msg) => msg.id === editingMessageId);
        if (messageToUpdate?.created) {
          // Update UI immediately
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === editingMessageId
                ? { ...msg, content: messageText.trim() }
                : msg
            )
          );

          // Send update to server in background
          await createSitesClientApi.tim_UpdateMessage({
            hqSiteId: siteId,
            projectIdMdm: projectIdMdm,
            message: messageText.trim(),
            created: messageToUpdate.created,
            modified: new Date(),
            taggedUsers: mentionedUsers,
            projectName,
            entity,
          });
        }
        setEditingMessageId(null);
      } else {
        // Send new message - optimistic update
        const tempMessage: Message = {
          id: `temp-${Date.now()}`,
          content: messageText.trim(),
          authorName: currentUserId || currentUserEmail.split('@')[0] || "You",
          authorId: currentUserId,
          timestamp: new Date(),
          isCurrentUser: true,
          created: new Date(),
        };

        // Add to UI immediately
        setMessages(prevMessages => [...prevMessages, tempMessage]);

        // Send to server in background
        await createSitesClientApi.tim_CreateMessage({
          hqSiteId: siteId,
          projectIdMdm: projectIdMdm,
          message: messageText.trim(),
          created: tempMessage.created,
          taggedUsers: mentionedUsers,
          projectName,
          entity,
        });
      }

      setMessageText("");
    } catch (error) {
      console.error("Failed to send message:", error);
      showSnackbar({ message: SnackbarMessage.FAILED_TO_SEND_MESSAGE, severity: "error" });
      // Reload messages on error to ensure consistency
      await loadMessages();
    } finally {
      setSending(false);
    }
  };

  const handleCancelMessage = () => {
    setMessageText("");
    setEditingMessageId(null);
    onClose();
  };

  const handleEditMessage = (messageId: string) => {
    if (!canSendMessage) return;
    const message = messages.find((msg) => msg.id === messageId);
    if (message) {
      setMessageText(message.content);
      setEditingMessageId(messageId);
    }
    handleCloseMenu();
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!canSendMessage) return;
    handleCloseMenu(); // Close menu first to prevent anchor issues

    try {
      const messageToDelete = messages.find((msg) => msg.id === messageId);
      if (!messageToDelete?.created) return;

      // Remove from UI immediately
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));

      // Send delete to server in background
       await createSitesClientApi.tim_DeleteMessage({
        hqSiteId: siteId,
        projectIdMdm: projectIdMdm,
        message: messageToDelete.content,
        created: messageToDelete.created,
      });
    } catch (error) {
      console.error("Failed to delete message:", error);
      showSnackbar({ message: SnackbarMessage.FAILED_TO_DELETE_MESSAGE, severity: "error" });
      // Reload messages on error to ensure consistency
      await loadMessages();
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    messageId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessageId(messageId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMessageId(null);
  };

  const formatTimestamp = (date: Date): string => {
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    const initials = parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || "")
      .join("");
    return initials || name[0]?.toUpperCase() || "?";
  };

  const isLastMessage = (messageId: string): boolean => {
    // Check if this message is the absolute last message in the conversation
    if (messages.length === 0) return false;
    const lastMessage = messages.at(-1);
    return lastMessage?.id === messageId;
  };

  const canEditOrDelete = (message: Message): boolean => {
    // User can only edit/delete their own message if it's the last message in the entire conversation
    return canSendMessage && message.isCurrentUser && isLastMessage(message.id);
  };

  // Check if rich text editor is empty (only contains empty HTML tags)
  const isInputEmpty = messageText.replaceAll(/<[^>]*>/g, '').trim() === "";

  const getButtonText = (): string => {
    return editingMessageId ? "Update" : "Send";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="messages-dialog"
      PaperProps={{
        sx: {
          height: "80vh",
          maxHeight: "700px",
        },
      }}
    >
      <DialogTitle className="messages-dialog-title">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <div>
            <div className="project-name">Messages</div>
            <div className="project-subtitle">{projectName}</div>
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className="messages-dialog-content" ref={messageContainerRef}>
        <div className="messages-container">
          {loading ? (
            <div className="no-messages">
              <CircularProgress size={40} />
              <p>Loading messages...</p>
            </div>
          ) : (
            <>
              {messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages yet.</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message-wrapper ${
                      message.isCurrentUser ? "user-message" : "other-message"
                    }`}
                  >
                    <div className="message-content-wrapper">
                      {message.isCurrentUser ? (
                        // Current user: timestamp above bubble
                        <>
                          <div className="message-header">
                            <span className="message-timestamp">
                              {formatTimestamp(message.timestamp)}
                            </span>
                          </div>
                          <div className="message-bubble-with-avatar">
                            <div className="message-bubble">
                              <div className="message-text" dangerouslySetInnerHTML={{ __html: message.content }} />
                              {canEditOrDelete(message) && (
                                <IconButton
                                  className="message-menu-button"
                                  size="small"
                                  onClick={(e) => handleOpenMenu(e, message.id)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              )}
                            </div>
                            <Avatar
                              className="message-avatar"
                              sx={{
                                bgcolor: "#00a3e0",
                                width: 32,
                                height: 32,
                                fontSize: 12,
                              }}
                            >
                              {getInitials(message.authorName)}
                            </Avatar>
                          </div>
                        </>
                      ) : (
                        // Other user: name and timestamp above bubble
                        <>
                          <div className="message-header">
                            <span className="message-author">{message.authorName}</span>
                            <span className="message-timestamp">
                              {formatTimestamp(message.timestamp)}
                            </span>
                          </div>
                          <div className="message-bubble-with-avatar">
                            <Avatar
                              className="message-avatar"
                              sx={{
                                bgcolor: "#00a3e0",
                                width: 32,
                                height: 32,
                                fontSize: 12,
                              }}
                            >
                              {getInitials(message.authorName)}
                            </Avatar>
                            <div className="message-bubble">
                              <div className="message-text" dangerouslySetInnerHTML={{ __html: message.content }} />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </DialogContent>

      <DialogActions className="messages-dialog-actions">
        {canSendMessage ? (
          <div className="message-input-container">
            <div className="rich-text-editor-wrapper">
              <HtmlEditor
                key={editingMessageId || 'new-message'}
                value={messageText}
                onValueChanged={(e: ValueChangedEvent) => {
                  setMessageText(e.value);
                }}
                placeholder="Type a message"
                height={150}
                className="message-input"
                disabled={sending}
              >
                <Toolbar>
                  <Item name="bold" />
                  <Item name="underline" />
                  <Item name="alignLeft" />
                  <Item name="alignCenter" />
                  <Item name="alignRight" />
                  <Item name="bulletList" />
                  <Item name="orderedList" />
                </Toolbar>
                <Mention
                  dataSource={mentionDataSource}
                  searchExpr="name"
                  displayExpr="name"
                  valueExpr="email"
                  marker="@"
                />
              </HtmlEditor>
            </div>

            <div className="message-actions-buttons">
              <Button
                variant="outlined"
                onClick={handleCancelMessage}
                disabled={sending}
                className="cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={isInputEmpty || sending}
                className="send-button"
              >
                {sending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  getButtonText()
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="message-input-container">
            <div className="message-actions-buttons">
              <Button
                variant="contained"
                onClick={onClose}
                className="send-button"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogActions>

      {/* Message options menu */}
      {anchorEl && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={() => {
              if (selectedMessageId) handleEditMessage(selectedMessageId);
            }}
          >
            <EditIcon fontSize="small" sx={{ marginRight: 1 }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedMessageId) handleDeleteMessage(selectedMessageId);
            }}
          >
            <DeleteIcon fontSize="small" sx={{ marginRight: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      )}
    </Dialog>
  );
};

export default MessagesDialog;